import {
	type DotLottie,
	type DotLottieReactProps,
	DotLottieReact as Player,
	setWasmUrl,
	type Theme,
} from '@lottiefiles/dotlottie-react';
import { useResolvedTheme } from '@sohanscript/theme/store';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

setWasmUrl('/wasm/dotlottie-player.wasm');

type ThemedLottieProps = Omit<
	DotLottieReactProps,
	'themeData' | 'className' | 'style'
> & {
	themeData?: Theme | string;
	themeId?: string;
	colorSlotTokens?: Record<string, string>;
	className?: string;
	style?: CSSProperties;
};

function ThemedLottie({
	themeData,
	themeId,
	colorSlotTokens,
	dotLottieRefCallback,
	className,
	style,
	...props
}: ThemedLottieProps) {
	const resolvedTheme = useResolvedTheme();
	const playerRef = useRef<DotLottie | null>(null);

	useEffect(() => {
		applyTheme(playerRef.current, themeData, themeId);
	}, [themeData, themeId]);

	useEffect(() => {
		const player = playerRef.current;
		if (!player || !resolvedTheme || !colorSlotTokens) return;

		function updateColorSlots() {
			if (!player) return;
			for (const [slotId, token] of Object.entries(colorSlotTokens ?? {})) {
				setColorSlot(player, slotId, getThemeColor(token));
			}
		}

		player.addEventListener('load', updateColorSlots);
		updateColorSlots();
		return () => player.removeEventListener('load', updateColorSlots);
	}, [colorSlotTokens, resolvedTheme]);

	function handleRef(player: DotLottie | null) {
		playerRef.current = player;
		applyTheme(player, themeData, themeId);
		dotLottieRefCallback?.(player);
	}

	return (
		<Player
			{...props}
			className={className}
			style={style}
			dotLottieRefCallback={handleRef}
		/>
	);
}

function applyTheme(
	player: DotLottie | null,
	themeData?: Theme | string,
	themeId?: string,
) {
	if (!player) return;
	if (themeData) player.setThemeData(themeData);
	if (themeId) player.setTheme(themeId);
}

function getThemeColor(
	token: string,
): [number, number, number, number] | undefined {
	if (typeof document === 'undefined') return;
	const color = getComputedStyle(document.documentElement)
		.getPropertyValue(token)
		.trim();
	if (!color || !CSS.supports('color', color)) return;
	const canvas = document.createElement('canvas');
	canvas.width = 1;
	canvas.height = 1;
	const context = canvas.getContext('2d', { willReadFrequently: true });
	if (!context) return;
	context.fillStyle = color;
	context.fillRect(0, 0, 1, 1);
	const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
	return [red / 255, green / 255, blue / 255, 1];
}

function setColorSlot(
	player: DotLottie,
	slotId: string,
	color: [number, number, number, number] | undefined,
) {
	if (color) player.setColorSlot(slotId, color);
}

export { ThemedLottie };
